import { useForm } from 'react-hook-form'
import { type ClipboardEvent, type MouseEvent } from 'react'

import { Button } from '@wartek-id/button-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'

import ImgErrorFixing from 'assets/icons/img-error-fixing.svg'

export const CONFIRMATION_TEXT =
  'Saya menyatakan bahwa saya menyetujui untuk mengulang perencanaan dari awal.'

interface PropsDialogDeleteSKP extends Omit<DialogProps, 'children'> {
  isLoading?: boolean
  onClickConfirm?: () => any
  onClickCancel?: () => any
}

const DialogDeleteSKP = ({
  isLoading,
  onClickConfirm = () => {},
  onClickCancel = () => {},
  ...props
}: PropsDialogDeleteSKP) => {
  const { register, formState, handleSubmit, reset, watch } = useForm<{
    confirmTextValue: string
  }>({
    defaultValues: {
      confirmTextValue: '',
    },
  })
  const { errors, isValid, isSubmitted, isSubmitSuccessful } = formState
  const { confirmTextValue } = watch()

  const isValidValues =
    !confirmTextValue || (!isValid && isSubmitted && !isSubmitSuccessful)

  const isDisabledConfirmationButton = isLoading || isValidValues

  const disableEvent = (
    e: ClipboardEvent<HTMLTextAreaElement> | MouseEvent<HTMLTextAreaElement>
  ) => {
    e.preventDefault()
  }

  return (
    <Dialog {...props}>
      <DialogOverlay />
      <DialogContent>
        <DialogCloseButton data-testid="btn-close" />
        <div className="mt-12 p-6">
          <ImgErrorFixing width={84} height={84} />
          <Text variant="display-sm" className="mt-4">
            Hapus perencanaan Anda dan ulang dari awal?
          </Text>
          <Text color="subdued" variant="body-lg" className="mt-4">
            Jika lanjut, perencanaan yang Anda sudah lakukan akan terhapus dan
            Anda harus mengulang perencanaan dari awal.
          </Text>
          <Text color="subdued" variant="body-lg" className="mt-4">
            Mohon ketik kalimat ini ke kolom kalimat persetujuan:
          </Text>

          <div className="bg-surface-selected-default p-1 my-4">
            <Text color="subdued" variant="body-lg">
              {CONFIRMATION_TEXT}
            </Text>
          </div>

          <Text className="mb-1">Kalimat persetujuan</Text>
          <Textarea
            {...register('confirmTextValue', {
              disabled: isLoading,
              validate(value) {
                return (
                  value === CONFIRMATION_TEXT ||
                  'Mohon cek ejaan dan coba lagi.'
                )
              },
            })}
            onPaste={disableEvent}
            onCut={disableEvent}
            onContextMenu={disableEvent}
            onCopy={disableEvent}
            placeholder="Ketik di sini."
            errorMessage={errors.confirmTextValue?.message}
            isInvalid={!!errors.confirmTextValue?.message}
          />

          <div className="flex gap-5 mt-5">
            <Button
              size="lg"
              className="w-[190px]"
              disabled={isDisabledConfirmationButton}
              isLoading={isLoading}
              onClick={handleSubmit(onClickConfirm)}
              data-testid="btn-confirm"
            >
              Lanjut hapus
            </Button>
            <Button
              size="lg"
              color="white"
              disabled={isLoading}
              isLoading={isLoading}
              onClick={() => {
                onClickCancel()
                reset()
              }}
              data-testid="btn-cancel"
            >
              Kembali
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default DialogDeleteSKP
